// @ts-check
/**
 * Persistence — what this origin's storage is, and ONE request that tries
 * every lever the browsers actually consult, from a user activation.
 *
 * **Measured, not assumed** (`experiments/storage/persist-probe/`,
 * 2026-09-07): `navigator.storage.persist()` answers by engine-specific
 * rules that a page mostly cannot influence —
 *
 * | Engine | How persistence is granted | What a page can do |
 * | --- | --- | --- |
 * | Chromium | no prompt; heuristics — site engagement, the site installed or bookmarked, **the notifications permission granted**; a refused request is re-evaluated on every later call | ask from an activation, and ask for the notifications permission (the one heuristic a page can trigger), then ask again |
 * | Firefox | the user answers a door-hanger | ask from an activation so the door-hanger appears; the promise stays pending until the user decides |
 * | WebKit | heuristics — the site opened as a Home Screen / Dock web app; no prompt; `permissions.query({ name: 'persistent-storage' })` throws | nothing in the page; say what to do outside it |
 *
 * `persist()` is not exposed in workers, so this module is for the page. The
 * Permissions API has `query` for `'persistent-storage'` (Chromium, Firefox)
 * and no `request` anywhere.
 *
 * `describePersistence()` reads everything without asking for anything;
 * `requestPersistence()` runs the whole ladder in one call, from a click, and
 * reports each step by name so the operator sees what happened and what is
 * left to try outside the page (bookmark, install, accept a prompt).
 *
 * Nothing here names an application.
 *
 * @module helpers/persistence
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Fable 5.1 (code generation)
 */

/**
 * @typedef {{
 *   isSecureContext: boolean,
 *   hasStorageManager: boolean,
 *   hasPersist: boolean,
 *   persisted: boolean | null,
 *   permission: 'granted' | 'denied' | 'prompt' | 'unsupported' | string,
 *   notifications: 'granted' | 'denied' | 'default' | 'unsupported',
 *   installed: boolean,
 *   userActivation: { isActive: boolean, hasBeenActive: boolean } | null,
 *   quotaBytes: number | null,
 *   usageBytes: number | null,
 * }} PersistenceDescription
 */

/**
 * @typedef {{ step: string, result: string | boolean | null, milliseconds?: number }} PersistenceStep
 * @typedef {PersistenceDescription & { steps: PersistenceStep[], advice: string[] }} PersistenceReport
 */

/** How long a pending `persist()` (Firefox's door-hanger) is waited for before the step is reported as pending. */
const PENDING_AFTER_MILLISECONDS = 15_000;

/** @param {Promise<any>} promise @param {number} milliseconds */
const settledWithin = (promise, milliseconds) => Promise.race([
    promise,
    new Promise(resolve => setTimeout(() => resolve('pending'), milliseconds)),
]);

/** @param {unknown} error */
const nameOf = error => `error: ${/** @type {any} */ (error)?.name ?? error}`;

/**
 * Reads the state without asking for anything.
 * @returns {Promise<PersistenceDescription>}
 */
export async function describePersistence() {
    const storage = navigator.storage;
    /** @type {PersistenceDescription} */
    const description = {
        isSecureContext: globalThis.isSecureContext === true,
        hasStorageManager: Boolean(storage),
        hasPersist: typeof storage?.persist === 'function',
        persisted: null,
        permission: 'unsupported',
        notifications: typeof Notification === 'function' ? Notification.permission : 'unsupported',
        installed: Boolean(globalThis.matchMedia?.('(display-mode: standalone)')?.matches
            || /** @type {any} */ (navigator).standalone === true),
        userActivation: navigator.userActivation
            ? { isActive: navigator.userActivation.isActive, hasBeenActive: navigator.userActivation.hasBeenActive }
            : null,
        quotaBytes: null,
        usageBytes: null,
    };
    if (storage?.persisted) {
        try { description.persisted = await storage.persisted(); } catch (error) { description.persisted = null; }
    }
    try {
        const status = await navigator.permissions?.query?.(/** @type {any} */ ({ name: 'persistent-storage' }));
        description.permission = status ? status.state : 'unsupported';
    } catch (error) {
        description.permission = 'unsupported';
    }
    if (storage?.estimate) {
        try {
            const estimate = await storage.estimate();
            description.quotaBytes = estimate.quota ?? null;
            description.usageBytes = estimate.usage ?? null;
        } catch { /* reported as null */ }
    }
    return description;
}

/**
 * The single test. Call it FROM a user activation (a click): it records the
 * state, asks `persist()`, and — where that is refused and the notifications
 * permission has never been decided — asks for that permission (Chromium's
 * one page-triggerable heuristic) and asks `persist()` again. Every step is
 * reported by name; nothing is retried silently.
 *
 * @param {{ askNotifications?: boolean }} [options] `askNotifications: false` skips the notifications step
 * @returns {Promise<PersistenceReport>}
 */
export async function requestPersistence({ askNotifications = true } = {}) {
    /** @type {PersistenceStep[]} */
    const steps = [];
    const before = await describePersistence();
    steps.push({ step: 'user activation at the request', result: before.userActivation ? `${before.userActivation.isActive}` : 'unsupported' });
    steps.push({ step: 'permission (persistent-storage) before', result: before.permission });
    steps.push({ step: 'persisted() before', result: before.persisted });

    const storage = navigator.storage;
    if (!before.hasPersist) {
        steps.push({ step: 'persist()', result: before.isSecureContext ? 'unsupported' : 'no navigator.storage — not a secure context' });
        return { ...before, steps, advice: adviceFor(before, steps) };
    }

    const ask = async (/** @type {string} */ label) => {
        const started = performance.now();
        let result;
        try { result = await settledWithin(storage.persist(), PENDING_AFTER_MILLISECONDS); }
        catch (error) { result = nameOf(error); }
        steps.push({ step: label, result, milliseconds: Math.round(performance.now() - started) });
        return result === true;
    };

    let granted = await ask('persist()');

    if (!granted && askNotifications && typeof Notification === 'function' && Notification.permission === 'default') {
        let outcome;
        try { outcome = await settledWithin(Notification.requestPermission(), PENDING_AFTER_MILLISECONDS); }
        catch (error) { outcome = nameOf(error); }
        steps.push({ step: 'Notification.requestPermission()', result: outcome });
        if (outcome === 'granted') granted = await ask('persist() after the notifications grant');
    }

    const after = await describePersistence();
    steps.push({ step: 'persisted() after', result: after.persisted });
    steps.push({ step: 'permission (persistent-storage) after', result: after.permission });
    return { ...after, steps, advice: adviceFor(after, steps) };
}

/**
 * What is left to try outside the page, by what the engine consults.
 * @param {PersistenceDescription} state
 * @param {PersistenceStep[]} steps
 */
function adviceFor(state, steps) {
    if (state.persisted === true) return [];
    /** @type {string[]} */
    const advice = [];
    if (!state.isSecureContext) {
        advice.push('Open the page over HTTPS or on localhost — a plain-HTTP address has no storage manager at all.');
        return advice;
    }
    const pending = steps.some(step => step.result === 'pending');
    if (pending) advice.push('The browser is asking you — answer its prompt, then check again.');
    if (!state.installed) advice.push('Install the site (Add to Dock / Home Screen / Install app) — Chromium and WebKit grant persistence to installed web apps.');
    advice.push('Bookmark the site — Chromium counts a bookmark towards granting persistence.');
    if (state.notifications === 'denied') advice.push('Notifications are denied for this site; Chromium counts a notifications grant towards persistence — allow them in the site settings if you want that lever.');
    advice.push('Keep using the site — Chromium grants persistence to sites with high engagement, and re-evaluates on every request.');
    return advice;
}

/**
 * One line per step, for a log.
 * @param {PersistenceReport} report
 */
export function formatPersistenceReport(report) {
    const lines = report.steps.map(step =>
        `${step.step}: ${step.result === null ? 'unknown' : String(step.result)}${step.milliseconds !== undefined ? ` (${step.milliseconds} ms)` : ''}`);
    lines.push(`installed: ${report.installed}; notifications: ${report.notifications}; quota: ${report.quotaBytes === null ? 'unknown' : `${(report.quotaBytes / 1024 ** 3).toFixed(1)} GiB`}`);
    return lines;
}
